import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const StockTransfersTab = () => {
  const navigate = useNavigate();
  const { 
    products, 
    stockItems,
    warehouses,
    fetchAllData,
    updateStockMovement,
    deleteStockMovement
  } = useAppData();
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAllData();
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };
    
    loadData();
    
    // Refresh data every 2 minutes
    const refreshInterval = setInterval(() => {
      loadData();
    }, 120000);
    
    // Clean up on unmount
    return () => clearInterval(refreshInterval);
  }, [fetchAllData]);

  // Extract stock transfers from stock items using real warehouse data
  const stockTransfers = useMemo(() => {
    if (!stockItems || !products) return [];
    
    return stockItems
      .filter(item => item.type === 'transfert')
      .map(transfer => {
        // Find the associated product
        const product = products.find(p => p.id === transfer.productId);
        const productName = product ? product.name : transfer.product || 'Produit inconnu';
        
        // Get source and destination locations using real warehouse data
        let sourceLocationName = transfer.sourceLocation || '';
        let destinationLocationName = transfer.destinationLocation || '';
        
        // Convert warehouse IDs to real names
        if (warehouses && warehouses.length > 0) {
          // For source location
          const sourceWarehouse = warehouses.find(w => String(w.id) === String(transfer.sourceLocation));
          if (sourceWarehouse) {
            sourceLocationName = sourceWarehouse.name;
          }
          
          // For destination location
          const destWarehouse = warehouses.find(w => String(w.id) === String(transfer.destinationLocation));
          if (destWarehouse) {
            destinationLocationName = destWarehouse.name;
          }
        }
        
        // If we still don't have names, use fallbacks
        if (!sourceLocationName) {
          sourceLocationName = 'Entrepôt source';
        }
        
        if (!destinationLocationName) {
          destinationLocationName = 'Entrepôt destination';
        }
        
        return {
          id: transfer.id,
          date: transfer.date || new Date().toISOString().split('T')[0],
          product: productName,
          productId: transfer.productId,
          quantity: parseInt(transfer.quantity) || 0,
          source: sourceLocationName,
          sourceId: transfer.sourceLocation,
          destination: destinationLocationName,
          destinationId: transfer.destinationLocation,
          reference: transfer.reference || `TR-${transfer.id.split('-')[1] || '000000'}`,
          status: transfer.status || 'in-transit',
          notes: transfer.notes || '',
          userId: transfer.userId || transfer.user || 'Admin'
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [products, stockItems, warehouses]);

  // Apply filters
  const filteredTransfers = useMemo(() => {
    return stockTransfers.filter(transfer => {
      // Match search term
      const matchesSearch = 
        !searchTerm || 
        transfer.product.toLowerCase().includes(searchTerm.toLowerCase()) || 
        transfer.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
        transfer.reference.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Match location filter
      const matchesLocation = 
        locationFilter === 'all' || 
        transfer.sourceId === locationFilter || 
        transfer.destinationId === locationFilter;
      
      // Match status filter
      const matchesStatus = statusFilter === 'all' || transfer.status === statusFilter;
      
      return matchesSearch && matchesLocation && matchesStatus;
    });
  }, [stockTransfers, searchTerm, locationFilter, statusFilter]);

  // Handle updating transfer status
  const handleUpdateStatus = (transferId, newStatus) => {
    try {
      // Find the transfer to update
      const transferToUpdate = stockTransfers.find(t => t.id === transferId);
      if (!transferToUpdate) return;
      
      // Update the transfer status
      const updatedTransfer = { ...transferToUpdate, status: newStatus };
      
      // When a transfer is marked as delivered, update the destination stock
      if (newStatus === 'delivered' && transferToUpdate.status !== 'delivered') {
        // This would typically be handled by the updateStockMovement function
        // which would then trigger a product stock update at the destination
        updateStockMovement(transferId, updatedTransfer);
        
        toast.success('Transfert marqué comme livré et stock mis à jour');
      } else {
        updateStockMovement(transferId, updatedTransfer);
        toast.success('Statut du transfert mis à jour');
      }
    } catch (error) {
      console.error('Error updating transfer status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };
  
  // Handle deleting transfer
  const handleDeleteTransfer = (transferId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce transfert ?')) {
      try {
        deleteStockMovement(transferId);
        toast.success('Transfert supprimé avec succès');
      } catch (error) {
        console.error('Error deleting transfer:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };
  
  // Handle creating a new transfer
  const handleNewTransfer = () => {
    navigate('/stock/transfer');
  };
  
  // Get status badge styles
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'En attente', className: 'bg-yellow-500/20 text-yellow-400' };
      case 'in-transit':
        return { label: 'En transit', className: 'bg-blue-500/20 text-blue-400' };
      case 'delivered':
        return { label: 'Livré', className: 'bg-green-500/20 text-green-400' };
      case 'cancelled':
        return { label: 'Annulé', className: 'bg-red-500/20 text-red-400' };
      default:
        return { label: status, className: 'bg-gray-500/20 text-gray-400' };
    }
  };

  // Use real warehouse data for locations
  const locations = useMemo(() => {
    if (warehouses && warehouses.length > 0) {
      return warehouses.map(warehouse => ({
        id: warehouse.id,
        name: warehouse.name
      }));
    } else {
      // Fallback if no warehouses data available
      return [
        { id: 'LOC001', name: 'Entrepôt Principal' },
        { id: 'LOC002', name: 'Magasin Centre' },
        { id: 'LOC003', name: 'Magasin Est' }
      ];
    }
  }, [warehouses]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Transferts de stock</h2>
          <p className="text-white/60 mt-1">
            Suivez les mouvements de stock entre différentes localisations
          </p>
        </div>
        <div>
          <button 
            onClick={handleNewTransfer}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center space-x-2"
          >
            <ArrowsRightLeftIcon className="h-5 w-5" />
            <span>Nouveau transfert</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Rechercher un transfert..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="relative">
          <div className="relative">
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">Toutes les localisations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white/50">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="relative">
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="in-transit">En transit</option>
              <option value="delivered">Livré</option>
              <option value="cancelled">Annulé</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white/50">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        <button 
          className={`px-3 py-1 rounded-full text-sm ${statusFilter === 'all' ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => setStatusFilter('all')}
        >
          Tous ({stockTransfers.length})
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm ${statusFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => setStatusFilter('pending')}
        >
          En attente ({stockTransfers.filter(t => t.status === 'pending').length})
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm ${statusFilter === 'in-transit' ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => setStatusFilter('in-transit')}
        >
          En transit ({stockTransfers.filter(t => t.status === 'in-transit').length})
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm ${statusFilter === 'delivered' ? 'bg-green-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => setStatusFilter('delivered')}
        >
          Livrés ({stockTransfers.filter(t => t.status === 'delivered').length})
        </button>
      </div>

      {/* Transfers Table */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          {filteredTransfers.length > 0 ? (
            <table className="w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Référence</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Quantité</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Destination</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredTransfers.map((transfer, index) => {
                  const statusBadge = getStatusBadge(transfer.status);
                  return (
                    <motion.tr
                      key={transfer.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        <div className="flex items-center">
                          <TagIcon className="h-5 w-5 mr-2 text-white/60" />
                          {transfer.reference}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {transfer.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        <div className="flex items-center">
                          <div 
                            className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm mr-2"
                          >
                            {transfer.product.charAt(0)}
                          </div>
                          <span>{transfer.product}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-white/80">
                        {transfer.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">
                        {transfer.source}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">
                        {transfer.destination}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button 
                            onClick={() => navigate(`/stock/movement/${transfer.id}`)}
                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors" 
                            title="Voir"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          
                          {/* Update Status Button - conditionally rendered based on current status */}
                          {transfer.status === 'pending' && (
                            <button 
                              onClick={() => handleUpdateStatus(transfer.id, 'in-transit')}
                              className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors" 
                              title="Marquer comme en transit"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                            </button>
                          )}
                          
                          {transfer.status === 'in-transit' && (
                            <button 
                              onClick={() => handleUpdateStatus(transfer.id, 'delivered')}
                              className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors" 
                              title="Marquer comme livré"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Only show delete for pending or in-transit */}
                          {(transfer.status === 'pending' || transfer.status === 'in-transit') && (
                            <button 
                              onClick={() => handleDeleteTransfer(transfer.id)}
                              className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors" 
                              title="Supprimer"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Show cancel option for pending or in-transit */}
                          {(transfer.status === 'pending' || transfer.status === 'in-transit') && (
                            <button 
                              onClick={() => handleUpdateStatus(transfer.id, 'cancelled')}
                              className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors" 
                              title="Annuler"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-white/60">
              <ArrowsRightLeftIcon className="h-12 w-12 mx-auto text-white/30 mb-4" />
              <p className="text-lg font-medium text-white mb-1">Aucun transfert trouvé</p>
              <p className="text-white/50 mb-4">
                {searchTerm || locationFilter !== 'all' || statusFilter !== 'all'
                  ? 'Aucun résultat ne correspond aux filtres sélectionnés'
                  : 'Vous n\'avez pas encore effectué de transferts de stock'}
              </p>
              <button
                onClick={handleNewTransfer}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors inline-flex items-center space-x-2"
              >
                <ArrowsRightLeftIcon className="h-5 w-5" />
                <span>Nouveau transfert</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockTransfersTab;